import React, { useEffect, useState, useRef } from "react";
import { Table, Button, Form, Row, Col, Card, Spinner, Modal } from "react-bootstrap";
import axios from "axios";
import API_BASE_URL from "../config";

const AttendanceView = ({ employeeId }) => {
  const [attendance, setAttendance] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [summary, setSummary] = useState({ summary: [], total_hours: 0 });
  const [dashboard, setDashboard] = useState({
    today_status: "absent",
    monthly: [],
  });
  const [fromDate, setFromDate] = useState(new Date().toISOString().split("T")[0]);
  const [toDate, setToDate] = useState(new Date().toISOString().split("T")[0]);
  const [period, setPeriod] = useState("week");
  const [loading, setLoading] = useState(false);
  const [shiftForm, setShiftForm] = useState({
    shift_id: "",
    start_date: "",
    end_date: "",
    rotation_pattern: "",
  });
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const [showCheckInOutModal, setShowCheckInOutModal] = useState(false);
  const [showAbsentModal, setShowAbsentModal] = useState(false);
  const [checkInOutAction, setCheckInOutAction] = useState("");
  const [checkInOutForm, setCheckInOutForm] = useState({
    time: new Date().toISOString().slice(0, 16),
    notes: "",
  });
  const [absentForm, setAbsentForm] = useState({
    date: selectedDate,
    notes: "",
  });
  const timeInputRef = useRef(null);
  const dateInputRef = useRef(null);

  useEffect(() => {
    fetchDashboard();
    fetchShifts();
    fetchAttendance();
  }, [employeeId]);

  const fetchDashboard = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/employees/${employeeId}/attendance_dashboard`);
      setDashboard(res.data);
    } catch (err) {
      console.error(err);
      alert("Failed to load dashboard");
    }
  };

  const fetchShifts = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/employees/${employeeId}/shifts`);
      setShifts(res.data);
    } catch (err) {
      console.error(err);
      alert("Failed to load shifts");
    }
  };

  const fetchAttendance = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_BASE_URL}/employees/${employeeId}/attendance`,
        { params: { from: fromDate, to: toDate } }
      );
      setAttendance(res.data);
    } catch (err) {
      console.error(err);
      alert("Failed to load attendance");
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/employees/${employeeId}/timesheet`,
        { params: { period, date: fromDate } }
      );
      setSummary(res.data);
    } catch (err) {
      console.error(err);
      alert("Failed to load timesheet");
    }
  };

  const handleCheckInOut = (action) => {
    setCheckInOutAction(action);
    setCheckInOutForm({ time: new Date().toISOString().slice(0, 16), notes: "" });
    setShowCheckInOutModal(true);
  };

  const handleCheckInOutSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_BASE_URL}/employees/${employeeId}/time_log`, {
        date: selectedDate,
        type: checkInOutAction,
        time: checkInOutForm.time,
        notes: checkInOutForm.notes,
      });
      setShowCheckInOutModal(false);
      alert(`Check-${checkInOutAction} Recorded`);
      fetchAttendance();
      fetchDashboard();
    } catch (err) {
      console.error(err);
      alert(`Failed to record check-${checkInOutAction}`);
    }
  };

  const handleMarkAbsent = () => {
    setAbsentForm({ date: selectedDate, notes: "" });
    setShowAbsentModal(true);
  };

  const handleAbsentSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_BASE_URL}/employees/${employeeId}/mark_absent`, {
        date: absentForm.date,
        notes: absentForm.notes,
      });
      setShowAbsentModal(false);
      alert("Marked Absent");
      fetchAttendance();
      fetchDashboard();
    } catch (err) {
      console.error(err);
      alert("Failed to mark absent");
    }
  };

  const handleShiftChange = (e) => {
    setShiftForm({ ...shiftForm, [e.target.name]: e.target.value });
  };

  const handleAssignShift = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_BASE_URL}/employees/${employeeId}/shifts`, shiftForm);
      alert("Shift Assigned");
      fetchShifts();
      setShiftForm({
        shift_id: "",
        start_date: "",
        end_date: "",
        rotation_pattern: "",
      });
    } catch (err) {
      console.error(err);
      alert("Failed to assign shift");
    }
  };

  return (
    <div>
      <h4>Attendance & Time Tracking</h4>
      <Card className="mb-3">
        <Card.Body>
          <Row>
            <Col md={3}>Today Status: {dashboard.today_status}</Col>
            {dashboard.monthly.map((stat, idx) => (
              <Col md={3} key={idx}>
                {stat.status}: {stat.count}
              </Col>
            ))}
          </Row>
        </Card.Body>
      </Card>
      <Row className="mb-3">
        <Col>
          <Button variant="success" onClick={() => handleCheckInOut("in")} className="me-2">
            Check In
          </Button>
          <Button variant="warning" onClick={handleMarkAbsent} className="me-2">
            Mark Absent
          </Button>
          <Button variant="danger" onClick={() => handleCheckInOut("out")}>
            Check Out
          </Button>
        </Col>
      </Row>

      {/* Check-In/Check-Out Modal */}
      <Modal
        show={showCheckInOutModal}
        onHide={() => setShowCheckInOutModal(false)}
        centered
        onEntered={() => timeInputRef.current?.focus()}
      >
        <Modal.Header closeButton>
          <Modal.Title>{`Enter Check-${checkInOutAction} Details`}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleCheckInOutSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Time</Form.Label>
              <Form.Control
                type="datetime-local"
                value={checkInOutForm.time}
                onChange={(e) => setCheckInOutForm({ ...checkInOutForm, time: e.target.value })}
                required
                ref={timeInputRef}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Notes (optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={checkInOutForm.notes}
                onChange={(e) => setCheckInOutForm({ ...checkInOutForm, notes: e.target.value })}
                placeholder="Enter notes"
              />
            </Form.Group>
            <Button variant="primary" type="submit">
              Submit
            </Button>
            <Button
              variant="secondary"
              onClick={() => setShowCheckInOutModal(false)}
              className="ms-2"
            >
              Cancel
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Mark Absent Modal */}
      <Modal
        show={showAbsentModal}
        onHide={() => setShowAbsentModal(false)}
        centered
        onEntered={() => dateInputRef.current?.focus()}
      >
        <Modal.Header closeButton>
          <Modal.Title>Mark Absent</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleAbsentSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Date</Form.Label>
              <Form.Control
                type="date"
                value={absentForm.date}
                onChange={(e) => setAbsentForm({ ...absentForm, date: e.target.value })}
                required
                ref={dateInputRef}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Notes (optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={absentForm.notes}
                onChange={(e) => setAbsentForm({ ...absentForm, notes: e.target.value })}
                placeholder="Enter notes"
              />
            </Form.Group>
            <Button variant="primary" type="submit">
              Submit
            </Button>
            <Button
              variant="secondary"
              onClick={() => setShowAbsentModal(false)}
              className="ms-2"
            >
              Cancel
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      <h5>Assign Shift</h5>
      <Form onSubmit={handleAssignShift}>
        <Row className="mb-3">
          <Col md={3}>
            <Form.Label>Shift</Form.Label>
            <Form.Select
              name="shift_id"
              value={shiftForm.shift_id}
              onChange={handleShiftChange}
              required
            >
              <option>Select Shift</option>
              <option value="1">Morning (09:00 - 17:00)</option>
            </Form.Select>
          </Col>
          <Col md={3}>
            <Form.Label>Start Date</Form.Label>
            <Form.Control
              type="date"
              name="start_date"
              value={shiftForm.start_date}
              onChange={handleShiftChange}
              required
            />
          </Col>
          <Col md={3}>
            <Form.Label>End Date (Optional)</Form.Label>
            <Form.Control
              type="date"
              name="end_date"
              value={shiftForm.end_date}
              onChange={handleShiftChange}
            />
          </Col>
          <Col md={3}>
            <Form.Label>Rotation Pattern</Form.Label>
            <Form.Control
              name="rotation_pattern"
              value={shiftForm.rotation_pattern}
              onChange={handleShiftChange}
              placeholder="e.g., weekly"
            />
          </Col>
        </Row>
        <Button type="submit">Assign Shift</Button>
      </Form>
      <h5 className="mt-4">Shifts</h5>
      <Table striped hover>
        <thead>
          <tr>
            <th>Name</th>
            <th>Start Time</th>
            <th>End Time</th>
            <th>Start Date</th>
            <th>End Date</th>
          </tr>
        </thead>
        <tbody>
          {shifts.map((shift) => (
            <tr key={shift.id}>
              <td>{shift.name}</td>
              <td>{shift.start_time}</td>
              <td>{shift.end_time}</td>
              <td>{shift.start_date}</td>
              <td>{shift.end_date || "Ongoing"}</td>
            </tr>
          ))}
        </tbody>
      </Table>
      <h5>Attendance Records</h5>
      <Row className="mb-3">
        <Col md={3}>
          <Form.Label>From</Form.Label>
          <Form.Control
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
        </Col>
        <Col md={3}>
          <Form.Label>To</Form.Label>
          <Form.Control
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
        </Col>
        <Col md={3}>
          <Form.Label>&nbsp;</Form.Label>
          <Button onClick={fetchAttendance}>Filter</Button>
        </Col>
      </Row>
      {loading ? (
        <Spinner animation="border" />
      ) : (
        <Table striped hover>
          <thead>
            <tr>
              <th>Date</th>
              <th>Total Hours</th>
              <th>Status</th>
              <th>Logs</th>
            </tr>
          </thead>
          <tbody>
            {attendance.map((rec) => (
              <tr key={rec.id}>
                <td>{rec.date}</td>
                <td>{rec.total_hours}</td>
                <td>{rec.status}</td>
                <td>
                  {rec.logs.map((log) => (
                    <div key={log.id}>
                      {log.type.toUpperCase()}: {log.time} - {log.notes || "No notes"}
                    </div>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      <h5>Timesheet Summary</h5>
      <Row className="mb-3">
        <Col md={3}>
          <Form.Select value={period} onChange={(e) => setPeriod(e.target.value)}>
            <option value="day">Day</option>
            <option value="week">Week</option>
            <option value="month">Month</option>
          </Form.Select>
        </Col>
        <Col md={3}>
          <Form.Control
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
        </Col>
        <Col md={3}>
          <Button onClick={fetchSummary}>Get Summary</Button>
        </Col>
      </Row>
      <p>Total Hours: {summary.total_hours.toFixed(2)}</p>
      <Table striped hover>
        <thead>
          <tr>
            <th>Date</th>
            <th>Hours</th>
          </tr>
        </thead>
        <tbody>
          {summary.summary.map((sum) => (
            <tr key={sum.date}>
              <td>{sum.date}</td>
              <td>{sum.hours || "0:00:00"}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

export default AttendanceView;